import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import GameOverviewTab from "@/components/game-tabs/GameOverviewTab";
import GameDownloadsTab from "@/components/game-tabs/GameDownloadsTab";
import GameStatisticsTab from "@/components/game-tabs/GameStatisticsTab"; // Új import
import GameCommentsTab from "@/components/game-tabs/GameCommentsTab";
import GameThanksTab from "@/components/game-tabs/GameThanksTab";
import GameHowTosTab from "./game-tabs/GameHowTosTab"; // Új tab komponens importálása
import { useAuth } from "@/context/AuthContext";
import AdPlaceholder from "@/components/AdPlaceholder";

import { BookText } from 'lucide-react'; // Új ikon importálása

import { Info, Download, MessageSquare, HeartHandshake, BarChart2 } from "lucide-react"; // Új ikon import
import { Button } from "@/components/ui/button"; // Importáljuk a Button komponenst


interface GameTabsProps {
  game: {
    developer?: string;
    publisher?: string;
    releaseDate?: string;
    id: number;
    name: string;
    hasAutotranslator?: boolean;
  };
  downloadsCount: number;
  commentsCount: number;
  thanksCount: number;
  uploads: number;
  activeTab: string;
  onValueChange: (tab: string) => void;
  targetCommentPage?: number | null;
  targetCommentId?: string | null;
  allow_comments?: boolean;
  ads?: boolean;
  reason?: string;
}

const GameTabs: React.FC<GameTabsProps> = ({ game, uploads = 0, commentsCount = 0, activeTab, thanksCount = 0, onValueChange, targetCommentPage, targetCommentId, allow_comments, reason }) => {
  const { isAdmin, isTranslator } = useAuth();
  const [howTosCount, setHowTosCount] = useState(0);

  useEffect(() => {
    const fetchHowTosCount = async () => {
      try {
        const response = await fetch(`/api/how-tos/count?game_id=${game.id}`);
        if (response.ok) {
          const data = await response.json();
          setHowTosCount(data.count);
        }
      } catch (error) {
        console.error("Hiba a segédletek számának lekérdezésekor:", error);
      }
    };
    fetchHowTosCount();
  }, [game.id]);

  return (
    <Tabs value={activeTab} onValueChange={onValueChange} className="w-full mt-8">
      {/* Asztali nézetű TabsList - kis képernyőkön elrejtve */}
      <TabsList className="hidden sm:flex w-full overflow-x-auto overflow-y-hidden whitespace-nowrap bg-secondary text-secondary-foreground">
        <TabsTrigger
          value="overview"
          className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none flex items-center justify-center gap-2 flex-shrink-0 px-2 py-2 text-sm sm:px-4"
        >
          <Info size={16} /> Adatlap
        </TabsTrigger>
        <TabsTrigger
          value="downloads"
          className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none flex items-center justify-center gap-2 flex-shrink-0 px-2 py-2 text-sm sm:px-4"
        >
          <Download size={16} /> Letöltések <span className="ml-1 inline-flex items-center justify-center h-4 w-auto min-w-[1rem] px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary/20 text-primary">{uploads.toLocaleString()}</span>
        </TabsTrigger>

        <TabsTrigger
          value="comments"
          className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none flex items-center justify-center gap-2 flex-shrink-0 px-2 py-2 text-sm sm:px-4"
        >
          <MessageSquare size={16} /> Hozzászólások <span className="ml-1 inline-flex items-center justify-center h-4 w-auto min-w-[1rem] px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary/20 text-primary">{commentsCount.toLocaleString()}</span>
        </TabsTrigger>

        <TabsTrigger
          value="thanks"
          className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none flex items-center justify-center gap-2 flex-shrink-0 px-2 py-2 text-sm sm:px-4"
        >
          <HeartHandshake size={16} /> Köszönöm <span className="ml-1 inline-flex items-center justify-center h-4 w-auto min-w-[1rem] px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary/20 text-primary">{thanksCount.toLocaleString()}</span>
        </TabsTrigger>

        <TabsTrigger
          value="statistics"
          className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none flex items-center justify-center gap-2 flex-shrink-0 px-2 py-2 text-sm sm:px-4"
        >
          <BarChart2 size={16} /> Statisztika
        </TabsTrigger>
        {(isTranslator || howTosCount > 0) && (
          <TabsTrigger
            value="how-tos"
            className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none flex items-center justify-center gap-2 flex-shrink-0 px-2 py-2 text-sm sm:px-4">
            <BookText size={16} /> Segédletek <span className="ml-1 inline-flex items-center justify-center h-4 w-auto min-w-[1rem] px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary/20 text-primary">{howTosCount.toLocaleString()}</span>
          </TabsTrigger>
        )}
      </TabsList>

      {/* Mobil nézetű gombok - kis képernyőkön látható */}
      <div className="sm:hidden grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        <Button
          variant={activeTab === "overview" ? "default" : "outline"}
          onClick={() => onValueChange("overview")}
          className={`
            ${activeTab === "overview" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border-border"}
            hover:bg-primary/80 hover:text-primary-foreground text-sm px-3 py-2 flex items-center justify-center gap-1
          `}
        >
          <Info size={16} /> Adatlap
        </Button>
        <Button
          variant={activeTab === "downloads" ? "default" : "outline"}
          onClick={() => onValueChange("downloads")}
          className={`
            ${activeTab === "downloads" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border-border"}
            hover:bg-primary/80 hover:text-primary-foreground text-sm px-3 py-2 flex items-center justify-center gap-1
          `}
        >
          <Download size={16} /> Letöltések <span className="ml-1 text-xs">{uploads.toLocaleString()}</span>
        </Button>

        <Button
          variant={activeTab === "comments" ? "default" : "outline"}
          onClick={() => onValueChange("comments")}
          className={`
            ${activeTab === "comments" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border-border"}
            hover:bg-primary/80 hover:text-primary-foreground text-sm px-3 py-2 flex items-center justify-center gap-1
          `}
        >

          <MessageSquare size={16} /> Hozzászólások <span className="ml-1 text-xs">{commentsCount.toLocaleString()}</span>
        </Button>
        <Button
          variant={activeTab === "thanks" ? "default" : "outline"}
          onClick={() => onValueChange("thanks")}
          className={`
            ${activeTab === "thanks" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border-border"}
            hover:bg-primary/80 hover:text-primary-foreground text-sm px-3 py-2 flex items-center justify-center gap-1
          `}
        >
          <HeartHandshake size={16} /> Köszönöm <span className="ml-1 text-xs">{thanksCount.toLocaleString()}</span>
        </Button>
        <Button
          variant={activeTab === "statistics" ? "default" : "outline"}
          onClick={() => onValueChange("statistics")}
          className={`
            ${activeTab === "statistics" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border-border"}
            hover:bg-primary/80 hover:text-primary-foreground text-sm px-3 py-2 flex items-center justify-center gap-1
          `}
        >
          <BarChart2 size={16} /> Statisztika
        </Button>
        {(isTranslator || howTosCount > 0) && (
          <Button
            variant={activeTab === "how-tos" ? "default" : "outline"}
            onClick={() => onValueChange("how-tos")}
            className={`
              ${activeTab === "how-tos" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground border-border"}
              hover:bg-primary/80 hover:text-primary-foreground text-sm px-3 py-2 flex items-center justify-center gap-1 col-span-2
            `}
          >
            <BookText size={16} /> Segédletek <span className="ml-1 text-xs">{howTosCount.toLocaleString()}</span>
          </Button>
        )}

      </div>

      <TabsContent value="overview" className="mt-4">
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mb-4" />}
        <GameOverviewTab game={game} />
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mt-4" />}
      </TabsContent>
      <TabsContent value="downloads" className="mt-4">
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mb-4" />}
        {uploads === 0 && game.hasAutotranslator ? (
          <div className="text-center p-8 bg-secondary rounded-lg">
            <p className="text-lg text-secondary-foreground">Ehhez a játékhoz még nincs feltöltött fordítás.</p>
            <p className="text-muted-foreground mt-2">Azonban elérhető hozzá <a href="/autotranslator" className="text-primary hover:underline font-bold">automata fordító program</a>, amivel lefordíthatod!</p>
          </div>
        ) : (
          <GameDownloadsTab gameId={game.id} gameTitle={game.name} />
        )}
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mt-4" />}
      </TabsContent>
      <TabsContent value="comments" className="mt-4">
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mb-4" />}
        <GameCommentsTab
          gameId={game.id}
          targetPage={targetCommentPage}
          targetCommentId={targetCommentId}
          allow_comments={allow_comments}
          reason={reason}
        />
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mt-4" />}
      </TabsContent>
      <TabsContent value="thanks" className="mt-4">
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mb-4" />}
        <GameThanksTab gameId={game.id} />
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mt-4" />}
      </TabsContent>
      <TabsContent value="statistics" className="mt-4">
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mb-4" />}
        <GameStatisticsTab gameId={game.id} />
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mt-4" />}
      </TabsContent>

      <TabsContent value="how-tos" className="mt-6">
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mb-4" />}
        <GameHowTosTab gameId={game.id} onHowTosCountChange={setHowTosCount} />
        {isAdmin && <AdPlaceholder height="h-32" text="" className="mt-4" />}
      </TabsContent>

    </Tabs>
  );
};

export default GameTabs;